import { ExternalReferenceFormModalComponent } from '../source-embedings/external-reference-form-modal/external-reference-form-modal.component';
import { NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow,MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { TSourceItem } from '@core/interfaces/TSourceItem';

import { TExternalReference, TSourceEmbedding } from '@core/api/sources/types';
import { CommonService } from '@shared/services/common.service';
import { SourcesService } from '@shared/services/sources.service';

import { ConfirmationModalComponent } from '../../../modals/confirmation-modal/confirmation-modal.component';
import { SourceReferencesSearchComponent } from '../source-references-search/source-references-search.component';

@Component({
    selector: 'app-source-references',
    templateUrl: './source-references.component.html',
    styleUrls: ['./source-references.component.scss'],
    standalone: true,
    imports: [
    SourceReferencesSearchComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatButton,
    MatIconButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatIcon,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelDescription,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatTooltip,
    NgTemplateOutlet,
  ]
})
export class SourceReferencesComponent implements OnDestroy {
  protected source: TSourceItem | undefined;

  protected isLoading = false;
  private _subs: Subscription[] = [];

  constructor(
    private _sourcesService: SourcesService,
    private commonService: CommonService,
    public dialog: MatDialog
  ) {}

  @Input() set sourceItem(source: TSourceItem) {
    this.source = source;
  }

  @Input() sourceEmbedding: TSourceEmbedding | undefined;

  /** `dialog` tightens layout for use inside the embedding references modal. */
  @Input() layout: 'page' | 'dialog' = 'page';

  protected trackRef(index: number, ref: TExternalReference): string {
    return ref._id ?? `idx-${index}-${ref.referenceText?.slice(0, 24) ?? ''}`;
  }

  protected referenceLabel(reference: TExternalReference, fallbackIndex: number): string {
    const allReferences = this.source?.externalReferences ?? [];
    const sourceIndex = allReferences.findIndex(item =>
      reference._id && item._id ? item._id === reference._id : item === reference
    );

    const resolvedIndex = sourceIndex >= 0 ? sourceIndex : fallbackIndex;
    return `1.${resolvedIndex + 1}`;
  }

  /** True when this reference is linked to the current embedding (embedding.externalReferences from attach API; citationIds may also list the same refs from ingestion). */
  protected isLinkedToCurrentEmbedding(reference: TExternalReference): boolean {
    const id = reference._id;
    if (!id || !this.sourceEmbedding) {
      return false;
    }
    const inRefs =
      this.sourceEmbedding.externalReferences?.some(value => value === id) ?? false;
    const inCitationIds =
      this.sourceEmbedding.citationIds?.some(value => value === id) ?? false;
    return inRefs || inCitationIds;
  }

  protected get linkedReferences(): TExternalReference[] {
    const all = this.source?.externalReferences ?? [];
    if (!this.sourceEmbedding) {
      return [];
    }
    return all.filter(ref => this.isLinkedToCurrentEmbedding(ref));
  }

  protected get unlinkedReferences(): TExternalReference[] {
    const all = this.source?.externalReferences ?? [];
    if (!this.sourceEmbedding) {
      return [];
    }
    return all.filter(ref => !this.isLinkedToCurrentEmbedding(ref));
  }

  addReferenceId(referenceId: string) {
    if (this.sourceEmbedding && this.sourceEmbedding.externalReferences) {
      this.sourceEmbedding.externalReferences.push(referenceId);
    }
  }

  removeReferenceId(referenceId: string) {
    if (this.sourceEmbedding && this.sourceEmbedding.externalReferences) {
      this.sourceEmbedding.externalReferences = this.sourceEmbedding.externalReferences.filter(
        id => id !== referenceId
      );
    }
  }

  loadReferences() {
    this.isLoading = true;
    this._sourcesService.getSource(this.source!._id, (res: TSourceItem) => {
      this.source = res;
      this.isLoading = false;
    });
  }

  deleteReference(reference: TExternalReference) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: { text: `Are you sure you want to delete this reference?` },
    });

    dialogRef.componentInstance.actionCallback.subscribe(() => {
      this._subs.push(
        this._sourcesService
          .deleteExternalReference(this.source!._id, reference._id!)
          .subscribe(() => {
            this.commonService.openSuccessSnackBar('Reference deleted');
            this.loadReferences();
          })
      );
      dialogRef.close();
    });
  }

  openModal(reference?: TExternalReference) {
    const dialRef = this.dialog.open(ExternalReferenceFormModalComponent, {
      width: 'min(520px, calc(100vw - 32px))',
      maxWidth: '100vw',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
      panelClass: 'external-reference-form-dialog',
      data: { source: this.source, reference },
    });
    dialRef.componentInstance.referenceChanged.subscribe(() => {
      if (reference) {
        this.commonService.openSuccessSnackBar('Reference updated');
      } else {
        this.commonService.openSuccessSnackBar('Reference added');
      }
      this.loadReferences();
    });
  }

  attachReference(reference: TExternalReference) {
    this.isLoading = true;
    this._subs.push(
      this._sourcesService
        .attachReference(this.sourceEmbedding!._id, reference._id!)
        .subscribe(() => {
          this.commonService.openSuccessSnackBar('Reference attached');
          this.isLoading = false;
          this.addReferenceId(reference._id!);
        })
    );
  }

  detachReference(reference: TExternalReference) {
    this.isLoading = true;
    this._subs.push(
      this._sourcesService
        .detachReference(this.sourceEmbedding!._id, reference._id!)
        .subscribe(() => {
          this.commonService.openSuccessSnackBar('Reference detached');
          this.isLoading = false;
          this.removeReferenceId(reference._id!);
        })
    );
  }

  setResults(results: TExternalReference[]) {
    if (this.source) {
      this.source!.externalReferences = results;
    }
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }
}
